import { useUserPlant } from '../hooks/User'
import { useShifts } from '../hooks/Shifts'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import objectSupport from 'dayjs/plugin/objectSupport'
import Papa from 'papaparse'
import { saveAs } from 'file-saver'
import { Button, Radio, Popover } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import { isEmpty } from 'lodash'
import { useEffect, useState } from 'react'
import { apiFetchDTCsForProblemLog } from '../api/dtcs-api'

dayjs.extend(objectSupport)
dayjs.extend(utc)

export const IssueExportButton = ({ issues, problemLogs, intl }) => {
  const [userPlant] = useUserPlant()
  const { isLoading: shiftsIsLoading, shifts: plantShifts } = useShifts({
    plant_name: userPlant,
  })
  const [dtcsByProblemLogId, setDtcsByProblemLogId] = useState({})

  const [readyToExport, setReadyToExport] = useState(false)
  useEffect(() => {
    const ready = !shiftsIsLoading && !isEmpty(problemLogs) && !isEmpty(issues)
    setReadyToExport(ready)
    if (!isEmpty(problemLogs)) {
      const newProbLogDTCs = {}
      const promises = []
      const probLogIDs = []
      Object.values(problemLogs).forEach((probLogs) => {
        probLogs.forEach((probLog) => {
          promises.push(apiFetchDTCsForProblemLog(probLog.id))
          probLogIDs.push(probLog.id)
        })
      })
      Promise.all(promises).then((values) => {
        for (let i = 0; i < values.length; i++) {
          const newProbLogID = probLogIDs[i]
          newProbLogDTCs[newProbLogID] = values[i]
        }
        setDtcsByProblemLogId(newProbLogDTCs)
      })
    }
  }, [issues, problemLogs, shiftsIsLoading])

  const setTimeToDate = (date, time) => {
    return date.clone().set({
      hour: time.hour(),
      minute: time.minute(),
      second: 0,
      millisecond: 0,
    })
  }

  // takes string of problem's created_at and returns shift name
  // assumes all are utc
  const getShiftName = (probTime) => {
    const probTm = dayjs.utc(probTime)
    for (let i = 0; i < plantShifts.length; i++) {
      const startHr = setTimeToDate(
        probTm,
        dayjs.utc(plantShifts[i].starts_at, 'HH:mm'),
      )
      const endHr = setTimeToDate(
        probTm,
        dayjs.utc(plantShifts[i].ends_at, 'HH:mm'),
      )
      if (startHr < endHr) {
        // shift doesn't wrap, simple comparison
        if (startHr <= probTm && probTm <= endHr) {
          return plantShifts[i].name
        }
      } else {
        // shift wraps
        const dayStart = probTm.startOf('day')
        const dayEnd = probTm.endOf('day')
        if (
          (dayStart <= probTm && probTm <= endHr) ||
          (startHr <= probTm && dayEnd)
        ) {
          return plantShifts[i].name
        }
      }
    }
  }

  const formatPossibleRootCause = (possibleRootCause) => {
    const nonEmptyItems = Object.keys(possibleRootCause).filter(
      (k) => possibleRootCause[k] !== null,
    )
    const lines = nonEmptyItems.map((k) => {
      let line = k
      const value = possibleRootCause[k]
      if (value) {
        line += `: ${value}`
      }
      return line
    })
    return lines.join(' -- ')
  }

  const formatProblemLogDTCs = (probLog) => {
    const dtcs = dtcsByProblemLogId[probLog.id]
    if (!isEmpty(dtcs)) {
      return dtcs
        .map((dtc) => {
          return `${dtc.acronym} - ${dtc.dtc}`
        })
        .join(', ')
    } else {
      return ''
    }
  }

  const createExportDataByAction = () => {
    const issueIdKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.issueId',
      defaultMessage: 'Issue ID',
    })
    const dateKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.date',
      defaultMessage: 'Date',
    })
    const issueStatusKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.issueStatus',
      defaultMessage: 'Issue Status',
    })
    const actionDescriptionKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.actionDescription',
      defaultMessage: 'Action',
    })
    const actionTypeKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.actionType',
      defaultMessage: 'Action Type',
    })
    const actionAssigneeKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.actionAssignee',
      defaultMessage: 'Assignee',
    })
    const actionStartDateKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.actionStartDate',
      defaultMessage: 'Start Date',
    })
    const actionDueDateKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.actionDueDate',
      defaultMessage: 'Due Date',
    })
    const actionStatusKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.actionStatus',
      defaultMessage: 'Status',
    })
    const actionCloseDateKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.actionCloseDate',
      defaultMessage: 'Close Date',
    })
    const cleanPointKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.cleanPoint',
      defaultMessage: 'Clean Point',
    })
    const responsibleKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.responsible',
      defaultMessage: 'Responsible',
    })
    const customerReferenceKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.customerReference',
      defaultMessage: 'Customer Reference',
    })
    const res = []
    for (let i = 0; i < issues.length; i++) {
      const issue = issues[i]
      const issueID = issue.id
      const issueStatus = issue.status
      const issueCreationDate = issue.created_at
      const actions = issue.actions
      const cleanPoint = issue.clean_point
      for (let j = 0; j < actions.length; j++) {
        let data = {}
        data[issueIdKey] = issueID
        data[dateKey] = issueCreationDate
        data[issueStatusKey] = issueStatus
        const action = actions[j]
        data[actionDescriptionKey] = action.description
        data[actionTypeKey] = action.type
        data[actionAssigneeKey] = action.assignee
        data[actionStartDateKey] = action.start_date
        data[actionDueDateKey] = action.due_date
        data[actionStatusKey] = action.status
        data[actionCloseDateKey] = action.close_date
        data[cleanPointKey] = cleanPoint
        data[responsibleKey] = issue.responsible
        data[customerReferenceKey] = issue.customer_reference
        res.push(data)
      }
    }
    return res
  }

  const createExportDataByVIN = () => {
    const issueIdKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.issueId',
      defaultMessage: 'Issue ID',
    })
    const issueDescriptionKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.issueDescription',
      defaultMessage: 'Description',
    })
    const vinKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.vins',
      defaultMessage: 'VINs',
    })
    const dateKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.date',
      defaultMessage: 'Date',
    })
    const modelKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.model',
      defaultMessage: 'Model',
    })
    const shiftKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.shifts',
      defaultMessage: 'Shifts',
    })
    const problemCodeKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.problemCode',
      defaultMessage: 'Problem Code',
    })
    const testZoneKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.testZones',
      defaultMessage: 'Test Zones',
    })
    const possibleRootCauseKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.possibleRootCause',
      defaultMessage: 'Possible Root Cause',
    })
    const directRootCauseKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.directRootCause',
      defaultMessage: 'Direct Root Cause',
    })
    const detectionRootCauseKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.detectionRootCause',
      defaultMessage: 'Detection Root Cause',
    })
    const systemicRootCauseKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.systemicRootCause',
      defaultMessage: 'Systemic Root Cause',
    })
    const dtcsKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.dtcs',
      defaultMessage: 'DTCs',
    })
    const responsibleKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.responsible',
      defaultMessage: 'Responsible',
    })
    const customerReferenceKey = intl.formatMessage({
      id: 'incidentMgmt.csvExportHeaders.customerReference',
      defaultMessage: 'Customer Reference',
    })
    const res = []
    for (let i = 0; i < issues.length; i++) {
      const issue = issues[i]
      const issueID = issue.id
      const issueDesc = issue.description
      const probLogs = problemLogs[issueID]
      const possibleRootCause = formatPossibleRootCause(
        issue.possible_root_causes,
      )
      const directRootCause = issue.direct_root_causes.direct_root_cause
      const detectionRootCause = issue.direct_root_causes.detection_root_cause
      const systemicRootCause = issue.direct_root_causes.systemic_root_cause
      for (let j = 0; j < probLogs.length; j++) {
        let data = {}
        data[issueIdKey] = issueID
        const probLog = probLogs[j]
        data[dateKey] = probLog.created_at
        data[vinKey] = probLog.vin
        data[modelKey] = probLog.model
        data[problemCodeKey] = probLog.problem_code
        data[shiftKey] = getShiftName(probLog.created_at)
        data[testZoneKey] = probLog.hardware_location_id
        data[issueDescriptionKey] = issueDesc
        data[possibleRootCauseKey] = possibleRootCause
        data[directRootCauseKey] = directRootCause
        data[detectionRootCauseKey] = detectionRootCause
        data[systemicRootCauseKey] = systemicRootCause
        data[dtcsKey] = formatProblemLogDTCs(probLog)
        data[responsibleKey] = issue.responsible
        data[customerReferenceKey] = issue.customer_reference
        res.push(data)
      }
    }
    return res
  }

  const exportIssues = () => {
    let data
    if (selectedReportType === 'VINs') {
      data = createExportDataByVIN()
    } else if (selectedReportType === 'Actions') {
      data = createExportDataByAction()
    }
    const dataString = Papa.unparse(data)
    const blob = new Blob(['\uFEFF' + dataString], {
      type: 'text/csv;charset=utf-8',
    })
    saveAs(blob, 'issues-report.csv')
  }

  const [selectedReportType, setSelectedReportType] = useState('VINs')
  const reportTypes = [
    {
      label: 'VINs by issue',
      value: 'VINs',
    },
    {
      label: 'Actions by issue',
      value: 'Actions',
    },
  ]

  const selectReportType = (e) => {
    setSelectedReportType(e.target.value)
  }
  const reportTypeSelection = (
    <>
      <Radio.Group
        options={reportTypes}
        defaultValue="VINs"
        onChange={selectReportType}
      />
      <br />
      <Button icon={<DownloadOutlined />} onClick={() => exportIssues()}>
        export
      </Button>
    </>
  )

  return (
    <Popover
      id="export-issue-popover"
      content={reportTypeSelection}
      title="Select Report Type"
      trigger="click"
    >
      <Button
        disabled={!readyToExport}
        //onClick={() => exportIssues()}
        icon={<DownloadOutlined />}
      >
        {intl.formatMessage({
          id: 'incidentMgmt.csvExport.exportLabel',
          defaultMessage: 'Export',
        })}
      </Button>
    </Popover>
  )
}
